import { axiosApiInstance } from "@/lib/axios";
import { API_PORTAL_URL } from "@/lib/config";

export interface IVisitor {
  ID: number;
  Name: string;
  Age: number;
  Gender: string;
  CountryID: number;
  StateID: number;
}

export interface ICartPaxPayload {
  SessionID: string;
  TicketPax: IVisitor[];
}

export interface IAddToCart {
  SessionID: string;
  CartType: "Ticket" | "Hotel";
  TicketID?: number;
  TicketPax?: {
    Name: string;
    Age: number;
    Gender: string;
    CountryID: number;
    StateID: number;
  }[];
  HotelID?: number;
  RoomCategoryID?: number;
  MealPlanID?: string | number;
  CheckIn?: string;
  CheckOut?: string;
  Night?: number;
  Rooms?: number;
  Adults?: number;
  Child?: number;
  ExtraAdult?: number;
  ExtraChild?: number;
  TotalAmount?: number;
  TicketTotal?: number;
  TotalAdults?: number;
  TotalChildSevenYr?: number;
  TotalChildThreeYr?: number;
  VisitDate?: string | null;
}

// Pax Management
export const insertCartPaxDetails = async (data: ICartPaxPayload) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Cart/InsertCartPaxDetails`,
    data,
  );
  return response.data;
};

export const deleteCartPaxDetails = async (ID: number) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Cart/DeleteCartPaxDetails`,
    { ID },
  );
  return response.data;
};

export const findByIDCartPaxDetails = async (SessionID: string) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Cart/FindByIDCartPaxDetails`,
    { SessionID },
  );
  const data = response.data?.Data || response.data;
  return data?.TicketPax || data?.CartPaxDetails || [];
};

// Cart Management
export const addToCart = async (data: IAddToCart) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Cart/AddToCart`,
    data,
  );
  return response.data;
};

export const deleteCart = async (ID: number, CartType: string = "Ticket") => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Cart/DeleteCart`,
    { ID, CartType },
  );
  return response.data;
};

export const findByIDCart = async (SessionID: string) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Cart/GetCartDetails`,
    { SessionID },
  );
  // Based on user feedback, the cart items might be at the root or under .Data
  const data = response.data?.Data || response.data;
  return {
    TicketCart: data?.TicketCart || [],
    HotelCart: data?.HotelCart || [],
    SOUTicketSoldOutDateforpax: data?.SOUTicketSoldOutDateforpax || [],
    VisitDate: data?.VisitDate || [],
  };
};

// Utility APIs
export const getAllCountries = async () => {
  const response = await axiosApiInstance.get(
    `${API_PORTAL_URL}/Country/FindAllActiveCountry`,
  );
  return response.data?.Data || [];
};

export const getStatesByCountry = async () => {
  const response = await axiosApiInstance.get(
    `${API_PORTAL_URL}/State/FindAllActiveState`,
  );
  return response.data?.Data || [];
};

export const getDistrictsByState = async (StateID: number) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/District/FindDisatrictByStateID`,
    { StateID },
  );
  return response.data?.Data || [];
};

// Booking & Payment APIs
export const insertTempBooking = async (data: any) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Booking/InsertTempBooking`,
    data,
  );
  return response.data;
};

export const createPaymentOrder = async (data: {
  Amount: number;
  Currency: string;
}) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/RazorpayGateway/CreateOrder`,
    data,
  );
  return response.data;
};

export const verifyPayment = async (data: any) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/RazorpayGateway/verify`,
    data,
  );
  return response.data;
};

export const insertBooking = async (data: { TempID: number }) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Booking/InsertBooking`,
    data,
  );
  return response.data;
};

export const findAvailableTicket = async (data: {
  TimeSlotID: number;
  Date: string;
}) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/SOUTicket/FindAvailableTicket`,
    data,
  );
  return response.data?.Data;
};

export const findByIDBooking = async (data: {
  ID: number;
  BookingStatus: string;
}) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Booking/FindByIDBooking`,
    data,
  );
  return response.data;
};

export const findAllAvailableHotelRoomPrice = async (data: {
  HotelID: number;
  RoomCategoryID: number;
  MealPlanID: string | number;
  FromDate: string;
  ToDate: string;
}) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Hotels/FindAllHotelPriceTicketAvaibility`,
    data,
  );
  return response.data;
};

export interface IUpdateTicketQuantity {
  SessionID: string;
  TotalAdults: number;
  TotalChildSevenYr: number;
  TotalChildThreeYr: number;
  ProjectionMappingID?: number | null;
  TimeSlotID?: number | null;
  TicketID?: number;
  VisitDate: string | null;
}

export const updateTicketQuantity = async (data: IUpdateTicketQuantity) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Cart/UpdateTicketQuantity`,
    data,
  );
  return response.data;
};

export interface IUpdateHotelQuantity {
  SessionID: string;
  HotelID: number;
  MealPlanID: number | string;
  RoomCategoryID: number;
  CheckIn: string | null;
  CheckOut: string | null;
  Night: number;
  Rooms: number;
  Adults: number;
  Child: number;
  ExtraAdult: number;
}

export const updateHotelQuantity = async (data: IUpdateHotelQuantity) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Cart/UpdateHotelQuantity`,
    data,
  );
  return response.data;
};

export const findAllSOUPackagePrice = async (data: {
  SOUPackageID: number;
  FromDate: string;
  ToDate: string;
}) => {
  const response = await axiosApiInstance.post(
    `${API_PORTAL_URL}/Hotels/FindAllSOUPackagePrice`,
    data,
  );
  return response.data;
};
